// The settings page "Labels": the person's labels in their order, made,
// renamed, recolored, moved and deleted here. A change is sent on blur / Enter
// / choosing (no save button); a refusal shows as a line under the row.
import { m } from "@trameria/i18n";
import { useState } from "react";
import type { SettingsItem } from "../app/screen-part";
import { apiErrorWithLead } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import { SettingsBox } from "../settings/settings-page";
import { ErrorLine } from "../ui/bits";
import { ICON_BUTTON } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import { Icon, iconOf } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { chipStyle } from "./chip";
import {
  LABEL_COLORS,
  LABEL_NAME_MAX,
  type Label,
  useCreateLabel,
  useDeleteLabel,
  useLabels,
  useRecolorLabel,
  useRenameLabel,
  useReorderLabels,
} from "./queries";

const FIELD =
  "plain-field min-w-[9rem] flex-1 rounded-[6px] border border-transparent bg-transparent px-1.5 py-[3px] text-[13px] text-fg outline-none hover:border-field focus:border-field-strong";

/** The colors to choose from, and one of your own (the picker's). */
function ColorChoice({
  value,
  name,
  onChange,
}: {
  value: string;
  name: string;
  onChange(color: string): void;
}) {
  return (
    <fieldset
      aria-label={m.labels_color_of({ name })}
      className="m-0 flex flex-wrap items-center gap-1 border-none p-0"
    >
      {LABEL_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={c.toLowerCase() === value.toLowerCase()}
          aria-label={c}
          onClick={() => onChange(c)}
          style={{ background: c }}
          className="h-[16px] w-[16px] flex-none cursor-pointer rounded-full border-2 border-transparent aria-pressed:border-fg"
        />
      ))}
      <input
        type="color"
        aria-label={m.labels_color_custom({ name })}
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#3b82f6"}
        onChange={(e) => onChange(e.target.value)}
        className="h-[18px] w-[18px] flex-none cursor-pointer rounded-[4px] border-none bg-transparent p-0"
      />
    </fieldset>
  );
}

function LabelRow({
  label,
  first,
  last,
  move,
  fail,
  failure,
}: {
  label: Label;
  first: boolean;
  last: boolean;
  move(by: -1 | 1): void;
  fail(at: string, text: string | null): void;
  failure: string | null;
}) {
  const rename = useRenameLabel();
  const recolor = useRecolorLabel();
  const remove = useDeleteLabel();
  const [name, setName] = useState(label.name);
  const pending = isOptimisticId(label.id);
  const save = () => {
    const next = name.trim();
    if (next === label.name) return;
    if (next === "") return setName(label.name);
    fail(label.id, null);
    rename.mutate(
      { id: label.id, name: next },
      {
        onError: (err) => {
          setName(label.name);
          fail(
            label.id,
            apiErrorWithLead(err, m.labels_rename_failed({ name: label.name })),
          );
        },
      },
    );
  };
  return (
    <div className="px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span
          aria-hidden="true"
          style={chipStyle(label.color)}
          className="h-[18px] w-[18px] flex-none rounded-[5px]"
        />
        <input
          type="text"
          aria-label={m.labels_name()}
          value={name}
          maxLength={LABEL_NAME_MAX}
          disabled={pending}
          onChange={(e) => setName(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (isImeComposing(e.nativeEvent)) return;
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setName(label.name);
          }}
          className={FIELD}
        />
        <ColorChoice
          value={label.color}
          name={label.name}
          onChange={(color) => {
            fail(label.id, null);
            recolor.mutate(
              { id: label.id, color },
              {
                onError: (err) =>
                  fail(
                    label.id,
                    apiErrorWithLead(
                      err,
                      m.labels_color_failed({ name: label.name }),
                    ),
                  ),
              },
            );
          }}
        />
        <span className="flex flex-none items-center">
          <button
            type="button"
            aria-label={m.labels_move_up({ name: label.name })}
            disabled={first || pending}
            onClick={() => move(-1)}
            className={`${ICON_BUTTON} disabled:opacity-30`}
          >
            <Icon kind="down" size={14} className="rotate-180" />
          </button>
          <button
            type="button"
            aria-label={m.labels_move_down({ name: label.name })}
            disabled={last || pending}
            onClick={() => move(1)}
            className={`${ICON_BUTTON} disabled:opacity-30`}
          >
            <Icon kind="down" size={14} />
          </button>
          <button
            type="button"
            aria-label={m.labels_delete({ name: label.name })}
            disabled={pending}
            onClick={async () => {
              const ok = await confirmAction({
                title: m.labels_delete_title({ name: label.name }),
                body: m.labels_delete_body(),
                action: m.common_delete(),
                danger: true,
              });
              if (!ok) return;
              fail(label.id, null);
              remove.mutate(
                { id: label.id },
                {
                  onError: (err) =>
                    fail(
                      label.id,
                      apiErrorWithLead(
                        err,
                        m.labels_delete_failed({ name: label.name }),
                      ),
                    ),
                },
              );
            }}
            className={ICON_BUTTON}
          >
            <Icon kind="close" size={14} />
          </button>
        </span>
      </div>
      {failure && <ErrorLine>{failure}</ErrorLine>}
    </div>
  );
}

function NewLabel({
  count,
  onError,
}: {
  count: number;
  onError(t: string | null): void;
}) {
  const create = useCreateLabel();
  const [name, setName] = useState("");
  const make = () => {
    const next = name.trim();
    if (next === "" || create.isPending) return;
    onError(null);
    create.mutate(
      { name: next, color: LABEL_COLORS[count % LABEL_COLORS.length] },
      {
        onSuccess: () => setName(""),
        onError: (err) =>
          onError(
            apiErrorWithLead(err, m.labels_create_failed({ name: next })),
          ),
      },
    );
  };
  return (
    <div className="flex items-center gap-2 px-4 py-2.5">
      <Icon kind="plus" size={13} className="flex-none text-muted" />
      <input
        type="text"
        aria-label={m.labels_new()}
        placeholder={m.labels_new()}
        value={name}
        maxLength={LABEL_NAME_MAX}
        onChange={(e) => setName(e.target.value)}
        onBlur={make}
        onKeyDown={(e) => {
          if (isImeComposing(e.nativeEvent)) return;
          if (e.key === "Enter") make();
        }}
        className={FIELD}
      />
    </div>
  );
}

function LabelsSettings() {
  const labels = useLabels();
  const reorder = useReorderLabels();
  const [failed, setFailed] = useState<{ at: string; text: string } | null>(
    null,
  );
  const fail = (at: string, text: string | null) =>
    setFailed(text === null ? null : { at, text });
  const items = labels.data ?? [];
  const move = (index: number, by: -1 | 1) => {
    const ids = items.map((l) => l.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + by, 0, id);
    fail("order", null);
    reorder.mutate(
      { ids },
      {
        onError: (err) =>
          fail("order", apiErrorWithLead(err, m.labels_move_failed())),
      },
    );
  };
  return (
    <>
      <SettingsBox>
        {items.map((l, i) => (
          <LabelRow
            key={l.id}
            label={l}
            first={i === 0}
            last={i === items.length - 1}
            move={(by) => move(i, by)}
            fail={fail}
            failure={failed?.at === l.id ? failed.text : null}
          />
        ))}
        <NewLabel count={items.length} onError={(t) => fail("new", t)} />
      </SettingsBox>
      {(failed?.at === "new" || failed?.at === "order") && (
        <ErrorLine>{failed.text}</ErrorLine>
      )}
    </>
  );
}

export const labelsSettings: SettingsItem = {
  id: "labels",
  order: 35,
  label: () => m.labels_title(),
  icon: iconOf("label"),
  description: () => m.labels_settings_description(),
  Component: LabelsSettings,
};
